import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { connect } from 'react-redux';
import {
  MotifButton,
  MotifDropdown,
  MotifDropdownItem,
  MotifIcon,
  MotifTooltip,
} from '@ey-xd/motif-react';
import { addAttribute } from '../../common/Attribute';
import * as formHelpers from '../../scripts/formHelpers';
import { normalizeReportPeriodObjects } from '../../common/ReportPeriodUtils';
import { IconoirNavArrowDown, IconoirInfoCircle } from '@ey-xd/motif-icon';

const TenantServiceHeader = ({
  reportPeriods,
  handleExportPropertiesTenantServices,
  handleUploadPropertiesTenantServices,
  handleEditPropertiesTenantServices,
  defaultReportPeriod,
  isActionsDisabled,
  isEditModeActive,
  isReportPeriodSpecific,
  noDataForReportPeriod,
}) => {
  useEffect(() => {
    addAttribute("button[role = 'combobox']", 'type', 'button');
  });

  const periodsInSortedOrder = [...reportPeriods].sort(
    (a, b) => new Date(b.endDate) - new Date(a.endDate)
  );
  const maxReportPeriod = periodsInSortedOrder[0]; // Get the max report period
  const uniquePeriodDescriptions = Array.from(
    new Set(periodsInSortedOrder.map((rp) => rp.reportPeriodDescription))
  ).map((periodDesc) => {
    return periodsInSortedOrder.find(
      (rp) => rp.reportPeriodDescription === periodDesc
    );
  });

  const rpOptions = uniquePeriodDescriptions.map((rp) => {
    return {
      key: rp.reportPeriodID,
      value: rp.reportPeriodDescription,
      text: rp.reportPeriodDescription,
      isSelected: rp.reportPeriodID === maxReportPeriod.reportPeriodID, // Set isSelected when IDs match
    };
  });

  const reportPeriodOptions = normalizeReportPeriodObjects(rpOptions, 'text');

  const [actionOptionsOpen, setActionOptionsOpen] = useState(false);

  return (
    <>
      {/* Header area */}
      <div style={{ minHeight: '24px' }}></div>
      {/* Form area */}
      <form>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            className="mr-3 mt-auto mr-2"
            style={{
              flex: '0 0 auto',
              marginBottom: '30px',
              minWidth: '200px',
            }}
          >
            {!isReportPeriodSpecific && (
              <Field
                name="reportPeriods"
                label="Report Period"
                options={reportPeriodOptions}
                component={formHelpers.SingleSelectComponent}
                testingData={{
                  label: 'lbReportPeriod',
                  div: 'divReportPeriod',
                }}
                defaultValue={defaultReportPeriod}
                disabled={isEditModeActive}
              />
            )}
          </div>
          <div
            className="mt-auto"
            style={{ flex: '0 0 auto', marginBottom: '-11px' }}
          >
            {!isReportPeriodSpecific && isEditModeActive && (
              <MotifTooltip
                placement="right"
                trigger={
                  <MotifIcon
                    iconFunction={IconoirInfoCircle}
                    size="24"
                    data-testid="InfoReportPeriod"
                    aria-label="Report Period Info"
                  />
                }
              >
                <div>Exit Edit mode to change the Report Period.</div>
              </MotifTooltip>
            )}
          </div>
          <div style={{ flex: '1' }}></div>
          {/* Hide actions dropdown if noDataForReportPeriod is true */}
          {!noDataForReportPeriod && (
            <div style={{ width: 'fit-content' }}>
              <MotifDropdown
                open={actionOptionsOpen}
                placement="bottom-end"
                id={'action-dropdown'}
                className="master-data-dropdown-action"
                handleClickOutside={() => setActionOptionsOpen(false)}
                trigger={
                  <MotifButton
                    type="button"
                    variant="primary"
                    onClick={() => setActionOptionsOpen(!actionOptionsOpen)}
                    aria-controls={'action-dropdown'}
                    id={'action-dropdown-trigger'}
                    aria-expanded={actionOptionsOpen}
                    aria-label="Action dropdown"
                    data-testid="btnActions"
                    disabled={isActionsDisabled || isEditModeActive}
                  >
                    Actions{' '}
                    <MotifIcon
                      iconFunction={IconoirNavArrowDown}
                      size="24"
                      fill="none"
                    />
                  </MotifButton>
                }
              >
                <ul role="menu">
                  <li role="menuitem">
                    <MotifDropdownItem
                      type="button"
                      onClick={handleExportPropertiesTenantServices}
                      data-testid="btnExportPropertiesTenantServicesAmenities"
                      className="master-dropdown-item"
                    >
                      Export Properties Tenant Services / Amenities
                    </MotifDropdownItem>
                  </li>
                  <li role="menuitem">
                    <MotifDropdownItem
                      type="button"
                      onClick={handleUploadPropertiesTenantServices}
                      data-testid="btnUploadPropertiesTenantServicesAmenities"
                      className="master-dropdown-item"
                    >
                      Upload Properties Tenant Services / Amenities
                    </MotifDropdownItem>
                  </li>
                  <li role="menuitem">
                    <MotifDropdownItem
                      type="button"
                      onClick={handleEditPropertiesTenantServices}
                      data-testid="btnEditPropertiesTenantServicesAmenities"
                      className="master-dropdown-item"
                      disabled={isEditModeActive}
                    >
                      Edit Properties Tenant Services / Amenities
                    </MotifDropdownItem>
                  </li>
                </ul>
              </MotifDropdown>
            </div>
          )}
        </div>
      </form>
    </>
  );
};

TenantServiceHeader.propTypes = {
  clientId: PropTypes.number,
  reportPeriods: PropTypes.array.isRequired,
  handleExportPropertiesTenantServices: PropTypes.func,
  handleUploadPropertiesTenantServices: PropTypes.func,
  handleEditPropertiesTenantServices: PropTypes.func,
  defaultReportPeriod: PropTypes.string,
  isActionsDisabled: PropTypes.bool,
  isEditModeActive: PropTypes.bool,
  isReportPeriodSpecific: PropTypes.bool,
  noDataForReportPeriod: PropTypes.bool,
};

export default connect((state) => ({
  bulkRollForwardFormValues: getFormValues('tenantServiceForm')(state),
}))(reduxForm({ form: 'tenantServiceForm' })(TenantServiceHeader));
